import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { Formik } from 'formik'
import { Box, Flex } from '@rebass/grid'
import { useGoogleLogin } from '@react-oauth/google'
import { useTheme } from 'styled-components'
import { FcGoogle } from 'react-icons/fc'
import Text from '../../atoms/Text'
import Input from '../../atoms/Input'
import Button from '../../atoms/Button'

const formValidation = (values) => {
  const { username, password } = values
  const errors = {}
  if (!username) {
    errors.username = '必須填寫'
  }
  if (!password) {
    errors.password = '必須填寫'
  }
  return errors
}

const LoginForm = ({ initialValues, onLogin, onGoogleLogin, forgotPasswordPath, registerPath, ...props }) => {
  const formikRef = useRef(null)
  const theme = useTheme()

  const onClickGoogleLogin = useGoogleLogin(
    useMemo(() => ({
      onSuccess: (data) => onGoogleLogin(data, formikRef.current || {}),
      flow: 'auth-code',
    }), [onGoogleLogin]),
  )

  return (
    <Formik
      innerRef={formikRef}
      validate={formValidation}
      initialValues={initialValues}
      onSubmit={onLogin}
      {...props}
    >
      {({ handleChange, handleBlur, handleSubmit, errors, values, isSubmitting, touched }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Box mt="0.6rem">
              <Input
                name="username"
                placeholder="用戶ID"
                value={get(values, 'username', '')}
                onChange={handleChange('username')}
                onBlur={handleBlur('username')}
                invalid={touched.username && !!errors.username}
              />
              <Box ml="0.8rem">
                <Text variant="body3" palette="error">{touched.username && !!errors.username ? errors.username : ' '}</Text>
              </Box>
            </Box>
            <Box mt="0.2rem">
              <Input
                name="password"
                placeholder="密碼"
                type="password"
                value={get(values, 'password', '')}
                onChange={handleChange('password')}
                onBlur={handleBlur('password')}
                invalid={touched.password && !!errors.password}
              />
              <Box ml="0.8rem">
                <Text variant="body3" palette="error">{touched.password && !!errors.password ? errors.password : ' '}</Text>
              </Box>
            </Box>
            {!!errors.form && (
              <Box ml="0.8rem">
                <Text palette="error">{errors.form}</Text>
              </Box>
            )}
            <Flex justifyContent="flex-end">
              <Button variant="text" to={forgotPasswordPath}>
                忘記密碼?
              </Button>
            </Flex>
            <Flex mt="0.2rem">
              <Button
                type="submit"
                disabled={!values.username || !values.password || isSubmitting}
                fullWidth
              >
                登入
              </Button>
            </Flex>
            {!!onGoogleLogin && (
              <Flex mt="1rem">
                <Button
                  palette="white"
                  onClick={onClickGoogleLogin}
                  style={{ border: '0.125rem solid #E0E0E0' }}
                  fullWidth
                >
                  <Text>使用 Google 登入</Text>
                  <Flex
                    width={24}
                    ml="0.5rem"
                    alignItems="center"
                    justifyContent="center"
                    style={{ position: 'relative' }}
                  >
                    <FcGoogle size={24} style={{ position: 'absolute' }} />
                  </Flex>
                </Button>
              </Flex>
            )}
            <Flex py="1.5rem" px="2rem" alignItems="center">
              <Box flex={1} height={1} bg={theme.palette.grayscale[4]} />
              <Box px="0.5rem">
                <Text palette="grayscale" paletteIndex={2}>或</Text>
              </Box>
              <Box flex={1} height={1} bg={theme.palette.grayscale[4]} />
            </Flex>
            <Button variant="outline" to={registerPath} fullWidth>
              新用戶註冊
            </Button>
          </form>
        )
      }}
    </Formik>
  )
}

LoginForm.propTypes = {
  initialValues: PropTypes.shape({
    username: PropTypes.string,
    password: PropTypes.string,
  }),
  onLogin: PropTypes.func,
  onGoogleLogin: PropTypes.func,
  forgotPasswordPath: PropTypes.string,
  registerPath: PropTypes.string,
}

LoginForm.defaultProps = {
  initialValues: {
    username: '',
    password: '',
  },
  onLogin: () => {},
  onGoogleLogin: null,
  forgotPasswordPath: '',
  registerPath: '',
}

export default LoginForm
